import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import logger from './logger';

// Platform-specific storage
const platformStorage = {
  async getItem(key: string): Promise<string | null> {
    if (Platform.OS === 'web') {
      return AsyncStorage.getItem(key);
    }
    return SecureStore.getItemAsync(key);
  },
  async setItem(key: string, value: string): Promise<void> {
    if (Platform.OS === 'web') {
      return AsyncStorage.setItem(key, value);
    }
    return SecureStore.setItemAsync(key, value);
  },
  async removeItem(key: string): Promise<void> {
    if (Platform.OS === 'web') {
      return AsyncStorage.removeItem(key);
    }
    return SecureStore.deleteItemAsync(key);
  },
};

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * Get stored authentication tokens
 */
export const getStoredTokens = async (): Promise<AuthTokens | null> => {
  try {
    const accessToken = await platformStorage.getItem('access_token');
    const refreshToken = await platformStorage.getItem('refresh_token');

    if (!accessToken || !refreshToken) {
      logger.debug('No tokens found in storage');
      return null;
    }

    logger.debug('Tokens retrieved successfully');
    return {
      accessToken,
      refreshToken,
    };
  } catch (error) {
    logger.error('Failed to get stored tokens:', error);
    return null;
  }
};

/**
 * Store authentication tokens
 */
export const storeTokens = async (tokens: AuthTokens): Promise<void> => {
  try {
    await platformStorage.setItem('access_token', tokens.accessToken);
    await platformStorage.setItem('refresh_token', tokens.refreshToken);
    logger.debug('Tokens stored successfully');
  } catch (error) {
    logger.error('Failed to store tokens:', error);
    throw error;
  }
};

/**
 * Remove stored tokens and user data
 */
export const clearTokens = async (): Promise<void> => {
  try {
    await platformStorage.removeItem('access_token');
    await platformStorage.removeItem('refresh_token');
    await platformStorage.removeItem('user_data');
    logger.debug('Tokens cleared successfully');
  } catch (error) {
    logger.error('Failed to clear tokens:', error);
  }
};

/**
 * Clear ALL user-specific data on logout
 * Prevents data leakage between different users on the same device
 */
export const clearAllUserData = async (): Promise<void> => {
  try {
    // Clear auth tokens + user profile
    await platformStorage.removeItem('access_token');
    await platformStorage.removeItem('refresh_token');
    await platformStorage.removeItem('user_data');

    // Clear AsyncStorage user data (cache, notification prefs, pending nav)
    const keysToRemove = [
      'notification_preferences',
      'alarms_silenced_until',
      '@pending_notification_nav',
      'pending_2fa',
    ];
    await AsyncStorage.multiRemove(keysToRemove);

    // Clear API response cache (all @IoTEdge:cache: entries)
    const allKeys = await AsyncStorage.getAllKeys();
    const cacheKeys = allKeys.filter((k) => k.startsWith('@IoTEdge:cache:'));
    if (cacheKeys.length > 0) {
      await AsyncStorage.multiRemove(cacheKeys);
    }

    logger.debug(`All user data cleared (${cacheKeys.length} cache entries removed)`);
  } catch (error) {
    logger.error('Failed to clear all user data:', error);
  }
};

// Export platform storage for use in AuthContext
export { platformStorage };
